'use client'
import { IColorsEntity, ISizesEntity } from '@/src/types/singleProduct'
import React, { useCallback, useMemo, useState } from 'react'
import { CheckIconSVG } from '@/src/ui/AllSVG'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'
import AddToCart from './AddToCart'
import { getClientCookieValue } from '@/src/utils/getClientCookieValue'



interface SingleProductCustomizationAndSelectionProps {
    sizes: ISizesEntity[];
    defaultColor: string;
    defaultSize: string;
    colors: IColorsEntity[];
    productId: string;
    variantSku: string;
    currency: string;
    alreadyCart: boolean;
    isVariant?: boolean;
    isExclusive?: boolean;
    stock?: number;
}
export default function SingleProductCustomizationAndSelection({ sizes, colors, defaultColor, defaultSize, productId, variantSku, currency, alreadyCart, isVariant, isExclusive, stock }: SingleProductCustomizationAndSelectionProps) {
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const country = getClientCookieValue('user_country_code');

    const initialColor = searchParams.get('color') ?? defaultColor
    const initialSize = searchParams.get('size') ?? defaultSize

    const [selectedColor, setSelectedColor] = useState<string>(initialColor)
    const [selectedSize, setSelectedSize] = useState<string>(initialSize)

    const updateURL = useCallback((colorName: string, sizeName: string) => {
        const params = new URLSearchParams(searchParams.toString())
        params.set('color', colorName)
        params.set('size', sizeName)
        router.replace(`${pathname}?${params.toString()}`, { scroll: false })
    }, [router, pathname, searchParams])

    const handleColourSelect = (colorName: string) => {
        setSelectedColor(colorName)
        updateURL(colorName, selectedSize ?? '')
    }

    const handleSizeSelect = (sizeName: string) => {
        setSelectedSize(sizeName)
        updateURL(selectedColor ?? '', sizeName)
    }

    const addToCartPayload = useMemo(() => {
        return {
            product_id: productId,
            quantity: 1,
            variant_sku: variantSku,
            country: country ?? 'NA',
            currency: currency,
        }
    }, [productId, variantSku, currency, country])

    return (
        <>
            {isVariant && (
                <div className='space-y-3 mb-8'>
                    {/* Colour picker */}
                    <div className="flex flex-col gap-3">
                        <p className="text-sm uppercase">
                            Color: <span className="font-normal tracking-normal capitalize">{selectedColor}</span>
                        </p>
                        <div className="flex flex-wrap gap-2.5">
                            {colors && colors?.length > 0 && colors?.map((color, index) => (
                                <button
                                    key={`${color?.name}-${index}`}
                                    title={color?.name}
                                    onClick={() => handleColourSelect(color?.name)}
                                    className={`relative rounded-full min-w-8 min-h-8 border-3 border-white shadow-sm flex items-center justify-center ${selectedColor === color?.name ? 'ring-1 ring-offset-1 ring-muted-color' : ''}`}
                                    style={{ backgroundColor: color?.code }}
                                >
                                    {selectedColor === color?.name && (
                                        <span className={`[&_svg_path]:fill-white [&_svg_path]:drop-shadow-sm ${color?.name?.toLowerCase() === 'white' ? '[&_svg_path]:fill-black!' : '[&_svg_path]:fill-white'}`}>
                                            <CheckIconSVG />
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Size picker */}
                    <div className="flex flex-col gap-3">
                        <p className="text-xs font-semibold tracking-widest uppercase">Size:</p>
                        <div className="flex flex-wrap gap-2.5">
                            {sizes && sizes?.length > 0 && sizes?.map((size, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleSizeSelect(size?.name)}
                                    className={`uppercase border border-muted-color rounded-md min-w-[48px] px-3.75 py-3.25 text-sm font-medium transition-colors
                                    ${selectedSize === size?.name
                                            ? 'bg-body-color text-white border-body-color    '
                                            : 'bg-white text-body-color border-body-color/20 hover:border-body-color'
                                        }`}
                                >
                                    {size?.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Add to bag */}
            <AddToCart addToCartPayload={addToCartPayload} isAlreadyCart={alreadyCart} isExclusive={isExclusive} stock={stock} />
        </>
    )
}
